import { DatabaseClient, getDatabaseClient } from '~/data/db/DatabaseClient';
import { resolveOfflineDb } from './offlineDatabase';
import {Archive, Document} from "~/types";
import QueryBuilder from "~/sqlite/services/queryBuilder";
import { diffByDataFieldSet } from '~/data/local/diffHelper';

export class DocumentsOfflineService {

    constructor(private readonly databaseClient: DatabaseClient | null = getDatabaseClient()) {}

    private readonly qb: QueryBuilder = new QueryBuilder();
    private get SQLite() {
        return resolveOfflineDb(this.databaseClient);
    }

    public reset(documents: Archive[], machineId: string, documentId: number): Promise<void> {
        const db = this.SQLite;
        if (!db) return Promise.resolve();

        return new Promise<void>((resolve, reject) => {
            this.checkDocuments(documents, machineId, documentId).then(({addDocuments, deleteDocuments}) => {
                if (!addDocuments.length && !deleteDocuments.length) return resolve();
                db.transaction(
                    (tx) => {
                        if (deleteDocuments.length) {
                            const { query, params } = this.qb.deleteIn('Documents', 'id', deleteDocuments);
                            tx.executeSql(query, params);
                        }
                        if (addDocuments.length) {
                            const { query, params } = this.qb.insert('Documents', addDocuments);
                            tx.executeSql(query, params);
                        }
                    },
                    (e) => reject(e),
                    resolve
                );
            });
        });
    }


    public checkDocuments(documentWithFile: Archive[], machineId: string, parentId: number): Promise<{ addDocuments: Document[]; deleteDocuments: string[] }> {
        const db = this.SQLite;
        if (!db) return Promise.resolve({ addDocuments: [], deleteDocuments: [] });

        const whereCondition = [
            { name: 'parentId', value: parentId.toString() },
            { name: 'machineId', value: machineId }
        ];
        const { query, params } = this.qb.select('Documents', [], whereCondition);

        return new Promise<{ addDocuments: Document[]; deleteDocuments: string[] }>((resolve, reject) => {
            db.executeSql(query, params, (resultSet) => {
                // Build new document objects for comparison
                const newDocuments: Document[] = documentWithFile.map((data) => {
                    const isLink = data.fileType === 'link';
                    return {
                        data: JSON.stringify({ ...data, downloadLink: '', filePath: isLink ? data.filePath : '' }),
                        documentId: data.id,
                        parentId,
                        machineId,
                        downloadLink: '',
                        filePath: isLink ? data.filePath : '',
                    };
                });

                const dbDocuments: Document[] = this.qb.getAllResult(resultSet);
                const { toAdd: addDocuments, toDeleteIds: deleteDocuments } = diffByDataFieldSet(
                    dbDocuments,
                    newDocuments
                );

                resolve({ addDocuments, deleteDocuments });
            }, (e) => reject(e));
        });
    }

    public getDocuments(machineId: string, parentId: number): Promise<Archive[]> {
        const db = this.SQLite;
        if (!db) return Promise.resolve([]);

        const whereCondition = [
            { name: 'parentId', value: parentId.toString() },
            { name: 'machineId', value: machineId }
        ];
        const { query, params } = this.qb.select('Documents', [], whereCondition);

        return new Promise<Archive[]>((resolve, reject) => {
            db.executeSql(
                query,
                params,
                (resultSet) => {
                    const documents = this.qb.getAllResult(resultSet).map((doc: any) => ({
                        ...JSON.parse(doc.data),
                        downloadLink: doc.downloadLink,
                        filePath: doc.filePath,
                    }));
                    resolve(documents);
                },
               (e) => reject(e)
            );
        });
    }

    public updateDocument(documentId: string, filename: string): void {
        const db = this.SQLite;
        if (!db) return;
        const updateData = { downloadLink: filename, filePath: filename };
        const whereCondition = [{name: 'documentId', value: documentId}];
        const { query, params } = this.qb.update(
            'Documents',
            updateData,
            whereCondition
        );
        db.executeSql(query, params);
    }
}
